import { useState, type ReactNode } from 'react';
import { passthrough } from '../primitives/passthrough.js';
import { cx } from '../primitives/cx.js';
import { Button } from '../primitives/Button.tsx';
import { Icon, type IconName } from '../primitives/Icon.tsx';

/**
 * RecordHeader - identity, two or three editable fields, and the quick actions.
 *
 * Renders before the timeline loads, which is why it takes its fields directly
 * rather than a render plan: the header is what tells a person they opened the
 * right record, and making them wait for a layout query to find that out is
 * the difference between a fast product and a slow one.
 */

export interface HeaderField {
  key: string;
  label: string;
  /** Already formatted. */
  value: string;
  editable: boolean;
}

export interface QuickAction {
  key: string;
  label: string;
  icon: IconName;
  onClick: () => void;
  /** Absent means available. A reason means the control is drawn disabled and says why. */
  unavailableReason?: string | undefined;
}

export interface RecordHeaderLabels {
  edit: string;
  save: string;
  cancel: string;
  more: string;
}

export interface RecordHeaderProps {
  /** The object this record is one of - "Company", "Deal". Drawn above the name. */
  kicker?: ReactNode;
  title: string;
  subtitle?: ReactNode;
  avatar?: ReactNode;
  fields: HeaderField[];
  actions: QuickAction[];
  /** The follow control and the next-step chip live here. */
  aside?: ReactNode;
  labels: RecordHeaderLabels;
  onSave?: ((changes: Record<string, string>) => Promise<void>) | undefined;
}

export function RecordHeader({
  kicker,
  title,
  subtitle,
  avatar,
  fields,
  actions,
  aside,
  labels,
  onSave,
}: RecordHeaderProps): JSX.Element {
  const [edits, setEdits] = useState<Record<string, string>>({});
  const editing = Object.keys(edits).length > 0;

  return (
    <header data-component="RecordHeader" className="flex flex-col gap-3 border-b border-b-rule pb-3">
      <div className="flex flex-wrap items-start gap-3">
        {avatar}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {/* Which kind of record this is, above the name of it. A person who
              followed a link from a report arrives knowing neither. */}
          {kicker ? (
            <p className="text-micro uppercase tracking-kicker text-bronze-text">{kicker}</p>
          ) : null}
          {/* The serif, because a record's title is its NAME - which is what
              the display face is for. */}
          <h1 className="serif-moment truncate text-h1 text-ink">{title}</h1>
          {subtitle ? <p className="text-dense text-ink-meta">{subtitle}</p> : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">{aside}</div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {actions.map((action) => (
          <Button
            key={action.key}
            data-record-action={action.key}
            intent="secondary"
            icon={action.icon}
            onClick={action.onClick}
            disabled={Boolean(action.unavailableReason)}
            // A disabled control that does not say why is a dead control with
            // a better disguise.
            title={action.unavailableReason}
          >
            {action.label}
          </Button>
        ))}
      </div>

      <dl className="flex flex-wrap gap-x-6 gap-y-2">
        {fields.map((field) => (
          <div key={field.key} data-header-field={field.key} className="flex min-w-0 flex-col gap-0.5">
            <dt className="text-micro uppercase tracking-label text-ink-label">{field.label}</dt>
            <dd className="flex items-center gap-1">
              {field.editable && onSave ? (
                <input
                  aria-label={field.label}
                  value={edits[field.key] ?? field.value}
                  onChange={(event) =>
                    setEdits((current) => ({ ...current, [field.key]: event.target.value }))
                  }
                  className={cx(
                    'min-w-0 rounded-control border border-transparent bg-transparent px-1 text-body text-ink',
                    'hover:border-rule focus-visible:border-rule focus-visible:outline-none',
                    '',
                  )}
                />
              ) : (
                <span className="truncate text-body text-ink">{field.value}</span>
              )}
            </dd>
          </div>
        ))}
      </dl>

      {editing && onSave ? (
        <div className="flex items-center gap-2">
          <Button intent="primary" onClick={() => void onSave(edits).then(() => setEdits({}))}>
            {labels.save}
          </Button>
          <Button intent="ghost" onClick={() => setEdits({})}>
            {labels.cancel}
          </Button>
        </div>
      ) : null}
    </header>
  );
}

/**
 * FollowButton - subscribe to a record (F-156).
 *
 * States the count as well as the state, because "following" alone does not
 * tell somebody whether anyone else is watching this record - which is the
 * thing people actually want to know before writing on it.
 */
export interface FollowButtonLabels {
  follow: string;
  following: string;
  followerCount: (count: string) => string;
}

export function FollowButton({
  following,
  followers,
  labels,
  onChange,
  ...rest
}: {
  following: boolean;
  /** Already formatted. */
  followers: string;
  labels: FollowButtonLabels;
  onChange: (following: boolean) => void;
} & Record<string, unknown>): JSX.Element {
  return (
    <span
      {...passthrough(rest)}
      data-component="FollowButton"
      className="inline-flex items-center gap-2"
    >
      <button
        type="button"
        data-following={following}
        aria-pressed={following}
        onClick={() => onChange(!following)}
        className={cx(
          'inline-flex h-touch items-center gap-1 rounded-control px-3 text-dense',
          'focus-visible:outline-none',
          following ? 'bg-bronze-tint text-bronze-text' : 'bg-sunken text-ink-600 hover:text-ink',
        )}
      >
        <Icon name={following ? 'BellRinging' : 'Bell'} size="inline" decorative />
        {following ? labels.following : labels.follow}
      </button>
      <span className="text-meta text-ink-meta">{labels.followerCount(followers)}</span>
    </span>
  );
}

/**
 * ConsentChips - per-channel consent, in three states.
 *
 * "Never asked" is not "said no". A workspace that cannot tell them apart
 * cannot run a compliant first campaign, and a rep about to dial needs to know
 * which of the two this is (P-3).
 */
export interface ConsentChipsLabels {
  optedIn: string;
  optedOut: string;
  unknown: string;
  /** Asked, and waiting for the person to confirm by email (F-082). Not consent. */
  pending: string;
  channel: Record<string, string>;
}

export function ConsentChips({
  consent,
  labels,
  ...rest
}: {
  consent: Array<{ channel: string; status: 'opted-in' | 'opted-out' | 'unknown' | 'pending' }>;
  labels: ConsentChipsLabels;
} & Record<string, unknown>): JSX.Element {
  return (
    <span
      {...passthrough(rest)}
      data-component="ConsentChips"
      className="inline-flex flex-wrap items-center gap-1"
    >
      {consent.map((entry) => (
        <span
          key={entry.channel}
          data-consent={entry.channel}
          data-consent-status={entry.status}
          className={cx(
            'inline-flex h-chip items-center gap-1 rounded-chip px-2 text-meta',
            entry.status === 'opted-in' && 'bg-success-tint text-success-text',
            entry.status === 'opted-out' && 'bg-danger-tint text-danger-text',
            entry.status === 'unknown' && 'bg-sunken text-ink-meta',
            entry.status === 'pending' && 'bg-warning-tint text-warning-text',
          )}
        >
          <Icon
            name={
              entry.status === 'opted-in'
                ? 'Check'
                : entry.status === 'opted-out'
                  ? 'Prohibit'
                  : entry.status === 'pending'
                    ? 'Hourglass'
                    : 'Question'
            }
            size="inline"
            decorative
          />
          {/*
            * "Email opted in", not "Email" in a green pill.
            *
            * The status used to be sr-only, so a sighted reader had the colour
            * and the icon and nothing else - and two pale tints six pixels
            * apart are not a distinction anybody should have to make to find
            * out whether they may email somebody. The design system's rule is
            * colour AND a word; a word only a screen reader gets is colour
            * alone for everyone else.
            */}
          {labels.channel[entry.channel] ?? entry.channel}{' '}
          <span className="lowercase">
            {entry.status === 'opted-in'
              ? labels.optedIn
              : entry.status === 'opted-out'
                ? labels.optedOut
                : entry.status === 'pending'
                  ? labels.pending
                  : labels.unknown}
          </span>
        </span>
      ))}
    </span>
  );
}
